import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormGroup,
  FormControlLabel,
  TextField,
  CircularProgress,
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import { DataGrid } from "@mui/x-data-grid";
import {
  fetchHospitalTours,
  editHospitalTour,
  updateHospitalTour,
  trashHospitalTour,
} from "../../Adapters/ApiAdapter";
import { baseImage_path, baseUrl } from "../../variables/Variables";

const ListHospitalTour = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [editImages, setEditImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [categoryName, setCategoryName] = useState("");
  const [loadingAction, setLoadingAction] = useState(false);
  const [newImages, setNewImages] = useState([]);
  const [editData, setEditData] = useState({
    id: null,
    category_name: "",
    selectedImages: [],
    newImages: [],
    videos: []
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);


  useEffect(() => {
    loadTours();
  }, []);

  const loadTours = async () => {
    try {
      const response = await fetchHospitalTours();
      setTours((response.data || []).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      )
      );
    } catch (err) {
      toast.error("Failed to load hospital tours.");
    } finally {
      setLoading(false);
    }
  };

  const handleImageCheckboxChange = (index) => {
    const updated = editImages.map((img, i) =>
      i === index ? { ...img, checked: !img.checked } : img
    );
    setEditImages(updated);

    const selectedPaths = updated
      .filter(img => img.checked)
      .map(img => img.path);

    setEditData(prev => ({
      ...prev,
      selectedImages: selectedPaths
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);

    const filtered = files.filter(file => {
      const fileName = file.name;
      const exists = editData.selectedImages.some(imgPath =>
        imgPath.endsWith(fileName)
      );
      return !exists;
    });

    if (filtered.length !== files.length) {
      toast.warning("Some duplicate images were skipped.");
    }

    const previews = filtered.map(file => ({
      preview: URL.createObjectURL(file),
      file
    }));

    setNewImages(prev => [...prev, ...previews]);
    setEditData(prev => ({
      ...prev,
      newImages: [...(prev.newImages || []), ...filtered]
    }));
  };

  const handleEdit = async (id) => {
    try {
      const response = await editHospitalTour(id);
      const images = JSON.parse(response.data.image || "[]").map((img) => ({
        path: img,
        checked: true,
      }));
      const vids = JSON.parse(response.data.video || "[]");

      setEditItem(response.data);
      setCategoryName(response.data.category_name);
      setEditImages(images);
      setVideos(vids);
      setNewImages([]); // Reset new images

      setEditData({
        id: response.data.id,
        category_name: response.data.category_name,
        selectedImages: images.map(img => img.checked ? img.path : null).filter(Boolean),
        newImages: [],
        videos: vids
      });

      setEditDialogOpen(true);
    } catch (err) {
      toast.error("Failed to load data for editing.");
    }
  };

  const handleDelete = async (id) => {
    setLoadingAction(true);
    try {
      const response = await trashHospitalTour(id);
      if (response.status === "true" || response.status === true) {
        toast.success("Tour deleted successfully!");
        loadTours();
      } else {
        toast.error(response.message || "Failed to delete.");
      }
    } catch (err) {
      toast.error("Error deleting tour.");
    } finally {
      setLoadingAction(false);
    }
  };

  // const handleSaveEdit = async () => {
  //   if (!categoryName.trim()) {
  //     return toast.error("Category Name is required.");
  //   }

  //   setLoadingAction(true);

  //   try {
  //     const formData = new FormData();
  //     formData.append("category_name", categoryName);

  //     // Append new image files
  //     editData.newImages.forEach((file) => {
  //       formData.append("image[]", file); // ✅ Correct key for multiple files
  //     });

  //     // Append existing images (as stringified paths, so backend knows to retain them)
  //     formData.append("existing_images", JSON.stringify(editData.selectedImages || []));

  //     // Append video URLs
  //     formData.append("video", JSON.stringify(editData.videos || []));

  //     // Debug formData
  //     for (let pair of formData.entries()) {
  //       console.log(`${pair[0]}:`, pair[1]);
  //     }

  //     const token = localStorage.getItem("token");
  //     const response = await fetch(`${baseUrl}api/update-hospital-tour/${editData.id}`, {
  //       method: "POST",
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //         Accept: "application/json",
  //       },
  //       body: formData,
  //     });

  //     const result = await response.json();

  //     if (result.status === "true" || result.status === true) {
  //       toast.success("Hospital Tour updated successfully!");
  //       setEditDialogOpen(false);
  //       setEditItem(null);
  //       setEditImages([]);
  //       setEditData({
  //         id: null,
  //         category_name: "",
  //         selectedImages: [],
  //         newImages: [],
  //         videos: []
  //       });
  //       loadTours(); // refresh list
  //     } else {
  //       toast.error(result.message || "Update failed.");
  //     }

  //   } catch (err) {
  //     console.error("Update error:", err);
  //     toast.error("Error updating hospital tour.");
  //   } finally {
  //     setLoadingAction(false);
  //   }
  // };

  const handleSaveEdit = async () => {
    if (!categoryName.trim()) {
      return toast.error("Category Name is required.");
    }

    setLoadingAction(true);

    try {
      const formData = new FormData();
      formData.append("category_name", categoryName);

      // ✅ Append existing image paths in a separate field
      formData.append("images[]", JSON.stringify(editData.selectedImages || []));

      // ✅ Append new image files to images[]
      (editData.newImages || []).forEach((file) => {
        formData.append("images[]", file); // file objects only
      });

      // ✅ Append video links
      formData.append("video", JSON.stringify(editData.videos || []));

      const token = localStorage.getItem("token");
      const response = await fetch(`${baseUrl}api/update-hospital-tour/${editData.id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: formData,
      });

      const result = await response.json();

      if (result.status === "true" || result.status === true) {
        toast.success("Hospital Tour updated successfully!");
        setEditDialogOpen(false);
        setEditItem(null);
        setEditImages([]);
        setEditData({
          id: null,
          category_name: "",
          selectedImages: [],
          newImages: [],
          videos: [],
        });
        loadTours();
      } else {
        toast.error(result.message || "Update failed.");
      }
    } catch (err) {
      console.error("Update error:", err);
      toast.error("Error updating hospital tour.");
    } finally {
      setLoadingAction(false);
    }
  };


  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "category_name", headerName: "Category Name", width: 200 },
    {
      field: "image",
      headerName: "Images",
      flex: 1,
      renderCell: (params) => {
        const imgs = JSON.parse(params.row.image || "[]");
        return (
          <Box>
            {imgs.slice(0, 3).map((img, i) => (
              <img
                key={i}
                src={`${baseImage_path}/${img}`}
                alt="Preview"
                style={{ width: 40, height: 40, marginRight: 4 }}
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row.id)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            style={{ marginLeft: 8 }}
            onClick={() => handleDelete(params.row.id)}
          >
            Trash
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Hospital Tour List
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <DataGrid
          rows={tours}
          columns={columns}
          getRowId={(row) => row.id}
          autoHeight
        />
      )}

      {editItem && (
        <Dialog open onClose={() => setEditItem(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Tour</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Category Name"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              margin="normal"
            />

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Existing Images
            </Typography>
            <FormGroup row>
              {editImages.map((img, i) => (
                <FormControlLabel
                  key={i}
                  control={
                    <Checkbox
                      checked={img.checked}
                      onChange={() => handleImageCheckboxChange(i)}
                    />
                  }
                  label={
                    <img
                      src={`${baseImage_path}/${img.path}`}
                      alt="Existing"
                      style={{ width: 80, height: 80, objectFit: "cover" }}
                    />
                  }
                />
              ))}
            </FormGroup>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Upload New Images
            </Typography>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageChange}
            />

            <FormGroup row sx={{ mt: 2 }}>
              {newImages.map((imgObj, index) => (
                <FormControlLabel
                  key={index}
                  control={<Checkbox checked disabled />}
                  label={
                    <img
                      src={imgObj.preview}
                      alt={`New-${index}`}
                      style={{ width: 80, height: 80, objectFit: "cover" }}
                    />
                  }
                />
              ))}
            </FormGroup>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Video Links
            </Typography>
            {videos.map((vid, index) => (
              <TextField
                key={index}
                fullWidth
                margin="dense"
                value={vid}
                onChange={(e) => {
                  const newVids = [...videos];
                  newVids[index] = e.target.value;
                  setVideos(newVids);
                  setEditData(prev => ({ ...prev, videos: newVids }));
                }}
              />
            ))}
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setEditItem(null)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveEdit}
              disabled={loadingAction}
            >
              {loadingAction ? <CircularProgress size={20} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ListHospitalTour;
